import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Clock, ArrowLeft, ArrowRight, BookOpen, Globe } from 'lucide-react';
import { useZakatGlobal } from '../context/ZakatGlobalContext';
import { CalculatorTopNav } from '../components/CalculatorTopNav';
import { MobileBottomNav } from '../components/MobileBottomNav';
import { HeaderShareButton } from '../components/HeaderShareButton';
import { ScholarlyPullQuote } from '../components/v2/ScholarlyPullQuote';
import { renderInlineLinks } from '../utils/renderInlineLinks';
import { ARTICLE_LOCALES, ARTICLE_LOCALE_META, getArticleBySlug, getRelatedArticles, getArticlesByCountry } from '../data/articles';
import { COUNTRY_BY_CODE } from '../data/articlesProgrammatic';
import { articleTranslationsFor } from '../data/articleTranslations';
import usePageMeta from '../hooks/usePageMeta';

/**
 * Article detail page — SEO-optimised long-form content with:
 *   • usePageMeta emitting title/description/canonical + hreflang alternates
 *   • JSON-LD Article + FAQPage + BreadcrumbList schemas in <head>
 *   • Language switcher covering all 21 article-content locales (does NOT
 *     change the app UI — only swaps the article body)
 *   • Related-articles rail + share button
 */
const ArticlePage = () => {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const { selectedCountry, selectedLanguage, countries, languages, updateCountry, updateLanguage, t } = useZakatGlobal();
  const article = getArticleBySlug(slug);
  const initialLang = params.get('lang') || 'en';
  const [lang, setLang] = useState(ARTICLE_LOCALES.includes(initialLang) ? initialLang : 'en');
  const meta = ARTICLE_LOCALE_META[lang] || ARTICLE_LOCALE_META.en;
  const isRTL = meta.dir === 'rtl';

  // Sync the ?lang= query param with state so bookmarking works.
  useEffect(() => {
    if (params.get('lang') !== lang) {
      const next = new URLSearchParams(params);
      if (lang === 'en') next.delete('lang'); else next.set('lang', lang);
      setParams(next, { replace: true });
    }
  }, [lang, params, setParams]);

  const content = useMemo(() => {
    if (!article) return null;
    return articleTranslationsFor(article, lang);
  }, [article, lang]);

  const canonicalUrl = `https://zakat.international/articles/${slug}`;

  usePageMeta({
    title: content ? content.seoTitle : 'Article — Amanah by Zakat.International',
    description: content ? content.seoDescription : '',
    keywords: content ? content.seoKeywords : '',
    canonical: lang === 'en' ? canonicalUrl : `${canonicalUrl}?lang=${lang}`,
  });

  // Inject Article + FAQPage + hreflang schemas as JSON-LD.
  useEffect(() => {
    if (!article || !content) return undefined;
    const ids = [];
    const addScript = (id, obj) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.id = id;
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      ids.push(id);
    };
    addScript(`article-jsonld-${slug}`, {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: content.seoTitle,
      description: content.seoDescription,
      inLanguage: lang,
      datePublished: article.published,
      dateModified: article.updated,
      author: { '@type': 'Organization', name: 'Amanah by Zakat.International' },
      publisher: { '@type': 'Organization', name: 'Amanah by Zakat.International', logo: { '@type': 'ImageObject', url: 'https://zakat.international/logo.png' } },
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
      articleSection: article.category,
      keywords: content.seoKeywords,
    });
    if (content.faq && content.faq.length) {
      addScript(`article-faq-jsonld-${slug}`, {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        inLanguage: lang,
        mainEntity: content.faq.map(({ q, a }) => ({
          '@type': 'Question',
          name: q,
          acceptedAnswer: { '@type': 'Answer', text: a },
        })),
      });
    }
    // HowTo schema for step-by-step "how to ..." articles.
    // If the article defines a howTo block (name + steps [{name, text, url?}]),
    // emit a schema.org HowTo. Otherwise, if the article's body starts with
    // "Step 1", "Step 2" ... sections we synthesise a HowTo from those.
    const synthesizedSteps = !article.howTo && Array.isArray(content.body)
      ? content.body
          .filter((sec) => /^step\s*\d+/i.test(sec.h || ''))
          .map((sec) => ({
            '@type': 'HowToStep',
            name: sec.h,
            text: sec.p,
            url: `${canonicalUrl}#${(sec.h || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
          }))
      : [];
    if (article.howTo || synthesizedSteps.length >= 2) {
      const steps = article.howTo?.steps || synthesizedSteps;
      addScript(`article-howto-jsonld-${slug}`, {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: article.howTo?.name || content.seoTitle,
        description: article.howTo?.description || content.seoDescription,
        inLanguage: lang,
        totalTime: article.howTo?.totalTime,        // ISO 8601 e.g. PT10M
        estimatedCost: article.howTo?.estimatedCost, // { currency, value } or undefined
        supply: article.howTo?.supply,              // [{@type:'HowToSupply', name:''}]
        tool: article.howTo?.tool,                  // [{@type:'HowToTool', name:''}]
        step: steps,
      });
    }
    // hreflang alternates via <link rel="alternate">
    const linkIds = [];
    ARTICLE_LOCALES.forEach((l) => {
      const id = `hreflang-${slug}-${l}`;
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = l;
      link.href = l === 'en' ? canonicalUrl : `${canonicalUrl}?lang=${l}`;
      link.id = id;
      document.head.appendChild(link);
      linkIds.push(id);
    });
    return () => {
      ids.forEach((id) => document.getElementById(id)?.remove());
      linkIds.forEach((id) => document.getElementById(id)?.remove());
    };
  }, [slug, lang, article, content, canonicalUrl]);

  if (!article || !content) {
    return (
      <div className="min-h-screen bg-stone-50">
        <CalculatorTopNav
          title={t ? t('nav.logoTitle') : 'Amanah by Zakat.International'}
          selectedCountry={selectedCountry}
          selectedLanguage={selectedLanguage}
          countries={countries}
          languages={languages}
          updateCountry={updateCountry}
          updateLanguage={updateLanguage}
          labels={{ zakat: 'Zakat', inheritance: 'Inheritance', will: 'Will' }}
          testidPrefix="article-nav"
        />
        <div className="max-w-3xl mx-auto px-4 pt-24 pb-20">
          <p className="text-center text-stone-600">Article not found.</p>
          <div className="text-center mt-4">
            <Link to="/articles" className="text-[#0D4733] underline">Browse all articles</Link>
          </div>
        </div>
      </div>
    );
  }

  const related = getRelatedArticles(slug, 3);
  const countryPeers = article.country ? getArticlesByCountry(article.country, slug, 8) : [];
  const countryMeta = article.country ? COUNTRY_BY_CODE[article.country] : null;
  const machineTranslated = lang !== 'en';

  return (
    <div className="gd-page min-h-screen" dir={isRTL ? 'rtl' : 'ltr'} lang={lang} data-testid="article-page">
      <CalculatorTopNav
        title={t ? t('nav.logoTitle') : 'Amanah by Zakat.International'}
        selectedCountry={selectedCountry}
        selectedLanguage={selectedLanguage}
        countries={countries}
        languages={languages}
        updateCountry={updateCountry}
        updateLanguage={updateLanguage}
        labels={{ zakat: 'Zakat', inheritance: 'Inheritance', will: 'Will' }}
        testidPrefix="article-nav"
      />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-24 pb-16">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-[11px] text-stone-500 mb-4" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#0D4733]">Home</Link>
          <span>/</span>
          <Link to="/articles" className="hover:text-[#0D4733]">Articles</Link>
          <span>/</span>
          <span className="text-stone-700 truncate max-w-[220px]" aria-current="page">{content.hero}</span>
        </nav>

        {/* Category + meta row */}
        <div className="flex items-center flex-wrap gap-2 text-[11px] font-mono uppercase tracking-wider text-stone-500 mb-3">
          <span className="px-2 py-0.5 rounded-full bg-[#EDF5F1] text-[#0D4733]">{article.category}</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{article.readingMinutes} min read</span>
          <span>·</span>
          <span>Updated {article.updated}</span>
        </div>

        <h1 className="font-heading text-2xl md:text-4xl leading-tight text-[#0D1520] mb-3" data-testid="article-hero">
          {content.hero}
        </h1>
        <p className="text-base md:text-lg text-stone-600 leading-relaxed mb-5">{content.subhero}</p>

        {/* Language switcher — article body only */}
        <div className="flex items-center gap-2 mb-6 flex-wrap p-2.5 rounded-lg bg-white border border-stone-200">
          <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-stone-500 font-mono">
            <Globe className="w-3 h-3" /> Article language
          </span>
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            data-testid="article-lang-select"
            className="px-2.5 py-1.5 rounded border border-stone-300 bg-white text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#0D4733]/40"
          >
            {ARTICLE_LOCALES.map((code) => (
              <option key={code} value={code}>{ARTICLE_LOCALE_META[code].name} — {ARTICLE_LOCALE_META[code].nameNative}</option>
            ))}
          </select>
          <div className="ml-auto"><HeaderShareButton title={content.hero} testid="article-share" /></div>
        </div>

        {machineTranslated && content.machineTranslated && (
          <div className="mb-6 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[12px] text-amber-900" data-testid="mt-banner">
            <span className="font-semibold">Machine-assisted translation:</span> the {ARTICLE_LOCALE_META[lang].nameNative} version was drafted with AI. For the reviewed version, please read the English canonical.
          </div>
        )}

        {/* Body */}
        <div className="prose prose-stone max-w-none" data-testid="article-body">
          {content.body.map((section, i) => (
            <section key={`s-${i}-${section.h.slice(0, 20)}`} className="mb-8">
              <div className="flex items-center gap-3 mt-8 mb-3">
                <span aria-hidden className="h-px w-8 bg-gold-500/70" />
                <h2 className="font-heading text-2xl md:text-3xl tracking-tight text-[#0D1520] leading-tight">{section.h}</h2>
              </div>
              <p className="text-[16px] md:text-[17px] leading-[1.85] text-stone-800">{renderInlineLinks(section.p, `body-${i}`)}</p>
            </section>
          ))}
        </div>

        {/* Key scholarly citation — visually anchored pull-quote */}
        {article.keyCitation && (
          <div className="mt-10">
            <ScholarlyPullQuote
              arabic={article.keyCitation.arabic}
              translation={article.keyCitation.translation}
              source={article.keyCitation.source}
              madhhab={article.keyCitation.madhhab}
              testid="article-key-citation"
            />
          </div>
        )}

        {/* FAQ */}
        {content.faq && content.faq.length > 0 && (
          <section className="mt-10 pt-6 border-t border-stone-200" data-testid="article-faq">
            <h2 className="font-heading text-xl md:text-2xl text-[#0D1520] mb-4">FAQ</h2>
            <ul className="space-y-3">
              {content.faq.map(({ q, a }, i) => (
                <li key={`q-${i}-${q.slice(0, 24)}`} className="border-b border-stone-100 last:border-b-0 pb-3 last:pb-0">
                  <p className="font-semibold text-[#0D1520]">{q}</p>
                  <p className="mt-1 text-stone-700 leading-relaxed">{renderInlineLinks(a, `faq-${i}`)}</p>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Related tools */}
        {article.relatedTools && article.relatedTools.length > 0 && (
          <section className="mt-10 p-4 rounded-2xl bg-[#EDF5F1] text-[#0D1520]">
            <h3 className="font-heading text-lg text-[#0D4733] mb-2">Try the free tools</h3>
            <div className="flex flex-wrap gap-2">
              {article.relatedTools.map(({ label, href }) => (
                <Link
                  key={label + href}
                  to={href}
                  className="px-3 py-1.5 rounded-full bg-gold-500 text-[#0D1520] text-sm font-semibold hover:bg-gold-400"
                >
                  {label} <ArrowRight className="inline w-3 h-3 ml-1" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Related in {country} — internal-linking block for programmatic
            country/asset articles. Boosts crawlability and time on site. */}
        {countryPeers.length > 0 && countryMeta && (
          <section className="mt-10" data-testid="article-country-peers">
            <div className="flex items-baseline gap-2 mb-3 flex-wrap">
              <h3 className="font-heading text-lg text-[#0D1520]">
                Related in {countryMeta.name.replace(/^the /, '')}
              </h3>
              <span className="text-[11px] font-mono uppercase tracking-wider text-stone-500">
                {countryPeers.length} guide{countryPeers.length === 1 ? '' : 's'}
              </span>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {countryPeers.map((r) => (
                <li key={r.slug}>
                  <Link
                    to={`/articles/${r.slug}${lang !== 'en' ? `?lang=${lang}` : ''}`}
                    className="flex items-start gap-2 p-3 rounded-lg border border-stone-200 hover:border-[#0D4733] hover:bg-[#F3EFEA] transition-colors"
                    data-testid={`country-peer-${r.slug}`}
                  >
                    <BookOpen className="w-4 h-4 mt-0.5 text-[#0D4733] shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-[#0D1520] leading-snug">{r.hero}</p>
                      <p className="text-[11px] text-stone-500 mt-0.5">{r.category} · {r.readingMinutes} min</p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Related articles */}
        {related.length > 0 && (
          <section className="mt-10">
            <h3 className="font-heading text-lg text-[#0D1520] mb-3">Related reading</h3>
            <ul className="space-y-2">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to={`/articles/${r.slug}${lang !== 'en' ? `?lang=${lang}` : ''}`}
                    className="flex items-start gap-2 p-3 rounded-lg border border-stone-200 hover:border-[#0D4733] hover:bg-[#F3EFEA] transition-colors"
                  >
                    <BookOpen className="w-4 h-4 mt-0.5 text-[#0D4733] shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-[#0D1520]">{r.hero}</p>
                      <p className="text-[11px] text-stone-500 mt-0.5">{r.category} · {r.readingMinutes} min</p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Back link */}
        <div className="mt-10 flex justify-between items-center pt-4 border-t border-stone-200">
          <Link to="/articles" className="inline-flex items-center gap-1 text-sm text-[#0D4733] hover:text-[#0D1520]">
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>
          <HeaderShareButton title={content.hero} testid="article-share-footer" />
        </div>
      </article>
      <div className="pb-24" />
      <MobileBottomNav />
    </div>
  );
};

export default ArticlePage;
